import {
  type PropertySubscription,
  type PropertyWithRelations,
} from '@markku/types'
import { Alert, Stack, Text, Title } from '@mantine/core'
import { IconInfoCircle } from '@tabler/icons-react'
import { PackageCard } from './PackageCard'

// Type for service packages from listActive endpoint
interface ServicePackageVersion {
  id: number
  priceMonthly: string
  description: string | null | undefined
  isActive: boolean
  packageVersionTasks?: Array<{
    id: number
    frequency: string | null | undefined
    maintenanceTask?: {
      id: number
      name: string
    }
  }>
}

interface ServicePackage {
  id: number
  name: string
  description: string | null
  versions?: ServicePackageVersion[]
}

interface PackageListSectionProps {
  packages: ServicePackage[] | undefined
  property: PropertyWithRelations | undefined
  activeSubscription: PropertySubscription | undefined
  proposalSubscription: PropertySubscription | undefined
  isCreatingCheckout: boolean
  onSubscribe: (versionId: number) => void
  canSubscribe?: boolean
}

export function PackageListSection({
  packages,
  property,
  activeSubscription,
  proposalSubscription: _proposalSubscription,
  isCreatingCheckout,
  onSubscribe,
  canSubscribe = true,
}: PackageListSectionProps) {
  // Check if property has floor area
  const existingData =
    property?.data && typeof property.data === 'object' ? property.data : {}
  const hasFloorArea =
    'floorArea' in existingData &&
    existingData.floorArea !== null &&
    existingData.floorArea !== undefined &&
    existingData.floorArea !== ''

  return (
    <Stack gap="lg">
      {activeSubscription && (
        <>
          <Title order={3}>Vaihda pakettia</Title>
          <Text>Voit vaihtaa toiseen pakettiin milloin vain.</Text>
        </>
      )}

      {!activeSubscription && (
        <>
          <Title order={3}>Valitse paketti</Title>
          <Text>
            Valitse kiinteistöllesi sopiva Markku-paketti. Hoitamme kaiken
            tarvittavan huollon ja pidämme kiinteistösi kunnossa.
          </Text>
        </>
      )}

      {packages?.map((pkg) => {
        const activeVersions = pkg.versions?.filter((v) => v.isActive)

        return (
          <div key={pkg.id}>
            {activeVersions?.map((version) => {
              const activePackageVersionId =
                activeSubscription?.subscriptionPackageVersions?.[0]
                  ?.servicePackageVersion?.id
              const isCurrentPackage = activePackageVersionId === version.id

              return (
                <PackageCard
                  key={version.id}
                  packageName={pkg.name}
                  packageDescription={pkg.description}
                  version={version}
                  isCurrent={isCurrentPackage}
                  isLoading={isCreatingCheckout}
                  onSubscribe={onSubscribe}
                  canSubscribe={canSubscribe}
                />
              )
            })}
          </div>
        )
      })}

      {!hasFloorArea && (
        <Alert
          color="blue"
          variant="light"
          icon={<IconInfoCircle />}
          title="Näetkö vain yhden paketin?"
        >
          <Text size="sm">
            Anna kiinteistösi kokonaispinta-ala yllä olevassa kortissa, niin
            näemme sinulle kaikki sopivat Markku-paketit.
          </Text>
        </Alert>
      )}
    </Stack>
  )
}
